import { lazy } from 'react';
import { Stack } from '@mui/material';
import { SiteContainer } from '@client/components/common/SiteContainer';
import { useMountEffect, useSetCurrentShow } from '@client/hooks/utils.hooks';
import { DocumentTitle } from '@client/components/common/DocumentTitle';
import { getCoverImage } from '@sda/utils/image';
import { resetBillingData } from '@client/slices/features/stripe.reducer';
import { useDispatch, useListeners } from '@client/hooks/redux.hooks';
import { Loader } from '@client/components/common/Loader';
import { useIsMobile } from '@client/hooks/dom.hooks';
import { withShow } from '@client/decorators/Public/withShow';
import { Show } from './Show';
import { NearbyShows } from './NearbyShows';
import { showsListeners } from './shows.listeners';
import { useBrowserFingerprint } from './useBrowserFingerprint.hook';
import { useAffiliateImpression } from './useAffiliateImpression.hook';
import type { RedeemableTicket } from '@sda/interfaces/ticket';

const LegacyDetailsLoader = lazy(() => import('../../../../../client/js/components/Shows/Details'));

export const Details = withShow<{ ticketRedemption?: RedeemableTicket }>()(({
  show, showId, ticketRedemption, ...restProps
}) => {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  useListeners(showsListeners);

  useBrowserFingerprint();

  useSetCurrentShow(showId);
  useAffiliateImpression(showId);

  useMountEffect(() => {
    dispatch(resetBillingData());
  });

  if (show.isOnlineOnly) {
    return (
      <Loader
        show={show}
        showId={showId}
        ticketRedemption={ticketRedemption}
        {...restProps}
        load={LegacyDetailsLoader}
      />
    );
  }

  return (
    <SiteContainer>
      <DocumentTitle
        title={show.title}
        description={show.bioHTML}
        image={getCoverImage(show.publicImageUrl)}
      />

      <Stack spacing={isMobile ? 4 : 6}>
        <Show
          {...show}
          ticketRedemption={ticketRedemption}
        />

        <NearbyShows
          id={showId}
          fuzzyLatLng={show.fuzzyLatLng}
        />
      </Stack>
    </SiteContainer>
  );
});

export default Details;
